import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Icon, Modal, NumberInput, Tooltip, useToast } from "../../components/ui";
import { useApiError } from "../../hooks/useApiError.js";
import { marketplaceIconSrc } from "../components/MarketplaceDonut";
import {
  PRICE_CHECK_MARKETPLACES,
  MARKETPLACE_LABELS,
  REVIEW_DAYS,
  STATE_TONE,
  TRADE_WINDOWS,
  getPriceCheck,
  searchRule,
  tradeGap,
} from "./priceCheck";
import {
  formatPrice,
  listingCurrency,
  listingIdentity,
  priceFigures,
  verdictCopy,
} from "./priceCheckCopy";
import { saveAccountPrice, saveChannelPrice, setReviewed } from "./priceCheckApi";
import useAccountChecks from "./useAccountChecks";
import usePriceCheckContext from "./usePriceCheckContext";
import PriceRangeBar from "./PriceRangeBar";
import "./price-check.scss";

const LOGO_KEY = { chrono: "chrono24", ebay: "ebay", wf: "watchfinder" };

/**
 * Verdict card: the state colour on its soft background. `trade` (tradeGap)
 * adds one line — the price against the trade median, green above / red below.
 */
export const PriceVerdict = ({ check, currency, trade = null }) => {
  const { title, body } = verdictCopy(check, currency);
  const fmt = (v) => formatPrice(Math.abs(v), currency);
  return (
    <div className={`pc-verdict pc-tone--${STATE_TONE[check.state]}`} role="status">
      <span className="pc-verdict__title">{title}</span>
      {trade ? (
        <span className={`pc-verdict__trade ${trade.diff >= 0 ? "is-up" : "is-down"}`}>
          {trade.diff === 0
            ? `At the ${trade.label} trade median (${fmt(trade.median)})`
            : `${fmt(trade.diff)} ${trade.diff > 0 ? "above" : "below"} ${trade.label} trade median (${fmt(trade.median)})`}
        </span>
      ) : null}
      <span className="pc-verdict__body">{body}</span>
    </div>
  );
};

/**
 * The marketplace's logo as a button that opens the very search the check ran
 * (`{mp}_url`). Renders nothing when the check has no search URL.
 */
export const MarketplaceLogoButton = ({ check, size = "md", className = "" }) => {
  if (!check?.url) return null;
  const name = MARKETPLACE_LABELS[check.marketplace] || check.label;
  return (
    <Button
      variant="secondary"
      size={size}
      href={check.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`pc-logo-btn pc-logo-btn--${check.marketplace} ${className}`.trim()}
      aria-label={`Open this search on ${name}`}
      title={`Open this search on ${name}`}
    >
      <img src={marketplaceIconSrc(LOGO_KEY[check.marketplace])} alt="" className="pc-logo-btn__img" />
    </Button>
  );
};

/** Hover / focus / long-press: the filters behind the search and what decides a verdict. */
export const CompCheckRule = ({ check }) => {
  const rule = searchRule(check.url);
  const converted = !check.real && check.fx && check.fx !== 1;
  const content = (
    <span className="pc-rule">
      <span className="pc-rule__title">{`Search on ${MARKETPLACE_LABELS[check.marketplace] || check.label}`}</span>
      {rule.length ? (
        <span className="pc-rule__list">
          {rule.map(([label, value]) => (
            <span key={label} className="pc-rule__item">
              <span className="pc-rule__label">{label}</span>
              <span className="pc-rule__value">{value}</span>
            </span>
          ))}
        </span>
      ) : (
        <span className="pc-rule__note">No search recorded for this check yet.</span>
      )}
      <span className="pc-rule__title">What shows here</span>
      <span className="pc-rule__note">Your price is compared with the 5 cheapest results of that search, your own listings left out.</span>
      <span className="pc-rule__note">Above market: higher than the 5th cheapest. Below market: lower than the cheapest. Otherwise in range.</span>
      <span className="pc-rule__note">Fewer than 2 results, or no price on this channel: no data, nothing flagged.</span>
      <span className="pc-rule__note">Checked nightly for active listings switched on for Chrono24 or eBay. Marking it reviewed hides it for {REVIEW_DAYS} days.</span>
      <span className="pc-rule__note">Trade median: 1M, else 3M, else 12M, else all time (PriceIQ trade prices, in GBP).</span>
      {converted ? (
        <span className="pc-rule__note">
          {`Converted from the CH24 UK search at 1 ${check.currency.toUpperCase()} = £${Number(check.fx).toFixed(4)}, rounded up to end in 50 or 00.`}
        </span>
      ) : null}
    </span>
  );
  return (
    <Tooltip content={content} side="top">
      <button type="button" className="pc-rule-trigger">Comp Check Rule</button>
    </Tooltip>
  );
};

const yearLabel = (row) => (row.year ? String(row.year) : "—");
// Chrono24's own wording, shortened: "Item is in stock" → In stock, etc.
const stockLabel = (row) => {
  const a = row.availability || "";
  if (/in stock/i.test(a)) return "In stock";
  if (/on request/i.test(a)) return "On request";
  if (/procure/i.test(a)) return "To procure";
  if (a) return a;
  return row.inStock === null ? "—" : row.inStock ? "In stock" : "Not in stock";
};

// CH24 Commission % — 5% unless the user has set their own, which is
// remembered per browser.
const COMMISSION_KEY = "rc:price-check:ch24-commission";
export const DEFAULT_COMMISSION = 5;
const readCommission = () => {
  try {
    const raw = window.localStorage.getItem(COMMISSION_KEY);
    const v = raw === null ? NaN : Number(raw);
    return Number.isFinite(v) && v >= 0 ? v : DEFAULT_COMMISSION;
  } catch (_) {
    return DEFAULT_COMMISSION;
  }
};
const writeCommission = (v) => {
  try {
    if (v === null || v === undefined) window.localStorage.removeItem(COMMISSION_KEY);
    else window.localStorage.setItem(COMMISSION_KEY, String(v));
  } catch (_) {
    // Storage blocked: the value just isn't remembered.
  }
};

/**
 * Price check panel — a dialog on desktop, a bottom sheet on phones (Modal
 * "auto"). Tabs: Chrono24 (CH24 UK), each other Chrono24 account switched on
 * for the listing (CH24 Europe… — its own search when the checker sent one,
 * else converted from the CH24 UK check), eBay, WatchFinder.
 *
 * `onListingChange(updated)` lets the opener refresh its card / row.
 * From the listing form: `livePrices` = { chrono, ebay, wf, accounts: {id: price} }
 * (unsaved form values, major units) and `onSetPrice(key, value, accountId)` —
 * the form fills its own field and its auto-save persists it, so the panel
 * never writes underneath an open form.
 */
export default function PriceCheckPanel({ open, onClose, listing, marketplace = "chrono", onListingChange, livePrices, onSetPrice }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { showApiError } = useApiError();
  const [key, setKey] = useState(marketplace);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [commission, setCommission] = useState(readCommission);
  const context = usePriceCheckContext(listing, { enabled: !!open });

  useEffect(() => {
    if (open) setKey(marketplace);
  }, [open, marketplace]);

  const { checks: accountChecks, legacy } = useAccountChecks(listing, {
    livePrices: livePrices?.accounts,
    enabled: !!open && !!listing?.id,
  });

  const tabs = useMemo(() => {
    if (!listing) return [];
    const base = PRICE_CHECK_MARKETPLACES.map((m) => {
      const hasLive = livePrices && Object.prototype.hasOwnProperty.call(livePrices, m);
      const check = getPriceCheck(listing, m, hasLive ? { priceOverride: livePrices[m] } : undefined);
      return { key: m, label: MARKETPLACE_LABELS[m], check, currency: listingCurrency(listing) };
    });
    const accounts = accountChecks
      .filter((a) => a.isListed || a.key === marketplace)
      .map((a) => ({ key: a.key, label: a.account.label, check: a.check, currency: a.check.currency, accountId: a.account.id }));
    // With other Chrono24 accounts on show, the main tab takes its account's name (CH24 UK).
    if (accounts.length && legacy?.label) base[0].label = legacy.label;
    return [base[0], ...accounts, base[1], base[2]];
  }, [listing, livePrices, accountChecks, legacy, marketplace]);

  const tab = tabs.find((t) => t.key === key) || tabs[0];
  const check = tab?.check;
  const suggested = check?.suggestedPrice ?? null;
  const own = check?.price ?? null;

  // The price field starts on the suggested price (out of range) or the current one.
  useEffect(() => {
    setDraft(suggested ?? own);
  }, [tab?.key, suggested, own]);

  if (!listing || !check) return null;

  const { brand, model, reference } = listingIdentity(listing);
  const currency = tab.currency;
  const fmt = (v) => formatPrice(v, currency);
  const hasData = check.lowestList.length >= 2;
  const draftValid = Number.isFinite(Number(draft)) && Number(draft) > 0;
  const label = tab.label;
  const isChrono = check.marketplace === "chrono";

  // Trade medians are GBP; shown in this tab's currency (fx = value of 1 unit in GBP).
  const fx = currency === "gbp" ? 1 : check.fx;
  const trade = isChrono ? tradeGap(check.price, context, fx) : null;
  const tradeCells = context && fx
    ? TRADE_WINDOWS.slice(0, 3).map(([k, l]) => {
        const v = context.trade_median?.[k];
        return [`${l} trade median`, v === null || v === undefined ? "—" : fmt(Math.round(v / fx))];
      })
    : [];
  const payout = draftValid && commission !== null ? Math.round(Number(draft) * (1 - commission / 100)) : null;
  const onCommission = (v) => {
    const next = v === null || !Number.isFinite(Number(v)) ? null : Math.min(Math.max(Number(v), 0), 100);
    setCommission(next);
    writeCommission(next);
  };

  const savePrice = async (value) => {
    if (tab.accountId) {
      const row = await saveAccountPrice(listing.id, tab.accountId, value);
      // The account price lives on its junction row — refresh every reader of it.
      queryClient.invalidateQueries({ queryKey: ["listing-marketplace-state", listing.id, tab.accountId] });
      return { listing, row };
    }
    return { listing: await saveChannelPrice(listing.id, check.marketplace, value) };
  };

  const onSet = async () => {
    if (!draftValid) return;
    const target = Number(draft);
    const previous = own;
    if (onSetPrice) {
      onSetPrice(tab.key, target, tab.accountId ?? null);
      toast.success(`${label} price set to ${fmt(target)} — saving with the listing`);
      return;
    }
    setBusy("set");
    try {
      const saved = await savePrice(target);
      onListingChange?.(saved.listing);
      toast.success(`${label} price set to ${fmt(target)}`, {
        action: previous !== null ? {
          label: "Undo",
          onClick: () => {
            savePrice(previous)
              .then((restored) => {
                onListingChange?.(restored.listing);
                toast.info(`${label} price back to ${fmt(previous)}`);
              })
              .catch((err) => showApiError(err, "restore the price"));
          },
        } : undefined,
      });
    } catch (err) {
      showApiError(err, "save the price");
    } finally {
      setBusy(null);
    }
  };

  const onMarkReviewed = async (on) => {
    setBusy("review");
    try {
      const updated = await setReviewed(listing, check.marketplace, on);
      onListingChange?.(updated);
      toast.success(on ? `${label} marked reviewed for ${REVIEW_DAYS} days` : `${label} back in the price review`);
    } catch (err) {
      showApiError(err, "update the price review");
    } finally {
      setBusy(null);
    }
  };

  const title = (
    <span className="pc-head">
      <span className="pc-head__eyebrow">Price check</span>
      <span className="pc-head__name">{[brand, model].filter(Boolean).join(" ") || "Listing"}</span>
    </span>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="auto"
      size="lg"
      className="pc-panel"
      title={title}
      description={(
        <>
          {reference ? `${reference} · ` : ""}Your price <strong className="pc-head__price">{fmt(check.price)}</strong>
        </>
      )}
      footer={(
        <div className="pc-actions">
          <div className="pc-actions__set">
            <NumberInput
              money
              currency={currency.toUpperCase()}
              size="md"
              value={draftValid ? Number(draft) : null}
              onChange={setDraft}
              min={1}
              aria-label={`New ${label} price`}
              disabled={!!busy}
            />
            <Button variant="primary" onClick={onSet} loading={busy === "set"} disabled={!draftValid || (!!busy && busy !== "set")}>
              Set Price
            </Button>
          </div>
          {isChrono ? (
            <div className="pc-actions__payout">
              <div className="pc-payout__field">
                <span className="pc-payout__label" id="pc-commission-label">CH24 Commission</span>
                <NumberInput
                  size="md"
                  value={commission}
                  onChange={onCommission}
                  min={0}
                  max={100}
                  decimals={1}
                  suffix="%"
                  placeholder="0"
                  aria-label="CH24 Commission (%)"
                />
              </div>
              <div className="pc-payout__field">
                <span className="pc-payout__label">CH24 Payout</span>
                <output className="pc-payout__value" aria-label="CH24 Payout">{payout === null ? "—" : fmt(payout)}</output>
              </div>
            </div>
          ) : null}
          <div className="pc-actions__row">
            <MarketplaceLogoButton check={check} />
            <Button variant="ghost" onClick={() => onMarkReviewed(check.state !== "reviewed")} loading={busy === "review"} disabled={!!busy && busy !== "review"}>
              {check.state === "reviewed" ? "Clear review" : `Mark reviewed · ${REVIEW_DAYS} days`}
            </Button>
          </div>
          <CompCheckRule check={check} />
        </div>
      )}
    >
      <div className="pc-tabs" role="tablist" aria-label="Marketplace">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab.key === t.key}
            className={`pc-tabs__tab${tab.key === t.key ? " is-on" : ""}`}
            onClick={() => setKey(t.key)}
          >
            <span className={`pc-dot pc-tone--${STATE_TONE[t.check.state]}`} aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="pc-body" data-testid={`price-check-${tab.key}`}>
        {check.fxMissing ? (
          <div className="pc-verdict pc-tone--neutral" role="status">
            <span className="pc-verdict__title">Rate unavailable</span>
            <span className="pc-verdict__body">{`There's no GBP → ${currency.toUpperCase()} rate right now, so the Chrono24 prices can't be converted. Nothing is flagged.`}</span>
          </div>
        ) : (
          <PriceVerdict check={{ ...check, label }} currency={currency} trade={trade} />
        )}

        {hasData && check.price !== null ? (
          <div className="pc-chart">
            <span className="pc-chart__caption">
              {`Your price vs ${check.count ?? check.lowestList.length} listings on ${label}`}
            </span>
            <PriceRangeBar check={check} currency={currency} />
          </div>
        ) : null}

        {hasData || tradeCells.length ? (
          <div className="pc-figs">
            {[...(hasData ? priceFigures(check, currency) : []), ...tradeCells].map(([figLabel, value]) => (
              <div key={figLabel} className="pc-fig">
                <span className="pc-fig__label">{figLabel}</span>
                <span className="pc-fig__value">{value}</span>
              </div>
            ))}
          </div>
        ) : null}

        {hasData ? (
          <div className="pc-cheapest has-details">
            <span className="pc-cheapest__title">{`Cheapest ${check.lowestList.length} on ${label}`}</span>
            <div className="pc-cheapest__row pc-cheapest__row--head" aria-hidden="true">
              <span className="pc-cheapest__rank">#</span>
              <span className="pc-cheapest__price">Price</span>
              <span className="pc-cheapest__year">Year</span>
              <span className="pc-cheapest__stock">Availability</span>
              <span className="pc-cheapest__open" />
            </div>
            {check.rows.map((row, i) => {
              // The seller's own asking price, when it is in another currency
              // than this site shows (Chrono24 converts with its own rate).
              const original = row.originalPrice && row.originalCurrency && row.originalCurrency !== currency
                ? formatPrice(row.originalPrice, row.originalCurrency)
                : null;
              const stock = stockLabel(row);
              return (
                <div key={`${row.price}-${i}`} className={`pc-cheapest__row${check.price !== null && row.price === check.price ? " is-you" : ""}`}>
                  <span className="pc-cheapest__rank">{i + 1}</span>
                  <span className="pc-cheapest__price">
                    {fmt(row.price)}
                    {original || row.country ? (
                      <span className="pc-cheapest__orig">{[original, row.country].filter(Boolean).join(" · ")}</span>
                    ) : null}
                  </span>
                  <span className="pc-cheapest__year" aria-label={`Year ${yearLabel(row)}`}>{yearLabel(row)}</span>
                  <span className={`pc-cheapest__stock${stock === "In stock" ? " is-in" : ""}`}>{stock}</span>
                  <span className="pc-cheapest__open">
                    {row.link ? (
                      <a href={row.link} target="_blank" rel="noopener noreferrer" className="pc-cheapest__link" aria-label={`Open listing ${i + 1} on Chrono24`} title="Open this listing">
                        <Icon name="ExternalLink" size="xs" />
                      </a>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
